import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@mui/material';
import { useGetAllArticlesQuery, useDeleteArticleMutation, useGetBlogStatsQuery, useLazyGetArticleByIdQuery } from 'store/services/blog/blogApi';
import { htmlToMarkdown, downloadMarkdownFile } from 'utils/downloadArticleMarkdown';
import { BlogArticleStatus } from 'types/interfaces/blog';
import AdminLayout from '../AdminLayout';
import MagicButton from 'components/MagicButton/MagicButton';
import Pagination from 'components/Pagination';
import ChevronDownIcon from 'components/Icons/ChevronDownIcon';
import './ArticlesListPage.css';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' }
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'publishedAt', label: 'Published Date' },
  { value: 'title', label: 'Title' }
];

const ArticlesListPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | BlogArticleStatus>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'publishedAt' | 'title'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<{ id: string; title: string } | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const pageSize = 20;

  const { data, isLoading, error, refetch } = useGetAllArticlesQuery({
    page,
    limit: pageSize,
    search: searchTerm,
    status: statusFilter === 'all' ? undefined : statusFilter,
    sortBy,
    sortOrder,
  });

  const { data: stats } = useGetBlogStatsQuery();

  const [deleteArticle, { isLoading: isDeleting }] = useDeleteArticleMutation();
  const [fetchArticle] = useLazyGetArticleByIdQuery();

  // Format date as MM/DD/YY
  const formatDate = (isoDateString: string | undefined): string => {
    if (!isoDateString) return '—';
    const date = new Date(isoDateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  // Get status badge class
  const getStatusClass = (status: BlogArticleStatus): string => {
    switch (status) {
      case 'PUBLISHED':
        return 'status-published';
      case 'DRAFT':
        return 'status-draft';
      case 'ARCHIVED':
        return 'status-archived';
      default:
        return '';
    }
  };

  // Handle download
  const handleDownload = async (id: string, slug: string, title: string) => {
    try {
      setDownloadingId(id);
      const article = await fetchArticle(id).unwrap();
      const markdownBody = htmlToMarkdown(article.contentHtml || '');
      const fileContent = `[${title}](https://trademagic.gg/articles/${slug})\n\n${markdownBody}`;
      downloadMarkdownFile(`${slug}.md`, fileContent);
    } catch (err) {
      console.error('Failed to download article:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle delete
  const handleDeleteClick = (id: string, title: string) => {
    setArticleToDelete({ id, title });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (articleToDelete) {
      try {
        await deleteArticle(articleToDelete.id).unwrap();
        setDeleteModalOpen(false);
        setArticleToDelete(null);
        // If deleting the last item on a page other than page 1, go to previous page
        if (data && data.data && data.data.length === 1 && page > 1) {
          setPage(page - 1);
        }
      } catch (err) {
        console.error('Failed to delete article:', err);
      }
    }
  };

  // Handle filter selection
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value as 'all' | BlogArticleStatus);
    setIsStatusDropdownOpen(false);
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value as 'createdAt' | 'publishedAt' | 'title');
    setIsSortDropdownOpen(false);
    setPage(1);
  };

  // Get selected labels
  const getSelectedStatusLabel = () => {
    const selected = STATUS_OPTIONS.find(opt => opt.value === statusFilter);
    return selected ? selected.label : 'All Statuses';
  };

  const getSelectedSortLabel = () => {
    const selected = SORT_OPTIONS.find(opt => opt.value === sortBy);
    return selected ? selected.label : 'Created Date';
  };

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout title="Admin Panel - Blog Articles" currentPath="/admin/blog/articles">
        <div className="articles-list-page">
          <div className="articles-list-header">
            <div>
              <h2 className="articles-list-title">Blog Articles</h2>
              <p className="articles-list-subtitle">
                Manage your blog content and publications
              </p>
            </div>
            <MagicButton
              label="New Article"
              onPress={() => {}}
              color="purple"
              isDisabled={true}
            />
          </div>

          {/* Stats cards skeleton */}
          <div className="articles-stats">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="stat-card">
                <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Skeleton variant="text" width={40} height={30} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              </div>
            ))}
          </div>

          <div className="articles-list-controls">
            <input
              type="text"
              placeholder="Search by title..."
              className="articles-search-input"
              disabled
            />
            <div className="articles-filter-controls">
              <div className="articles-filter-dropdown">
                <span>All Statuses</span>
                <ChevronDownIcon className="dropdown-arrow" />
              </div>
              <div className="articles-filter-dropdown">
                <span>Created Date</span>
                <ChevronDownIcon className="dropdown-arrow" />
              </div>
            </div>
          </div>

          <div className="articles-list-content">
            <div className="articles-table">
              <div className="articles-table-header">
                <div className="articles-col-title">Title</div>
                <div className="articles-col-status">Status</div>
                <div className="articles-col-date">Published</div>
                <div className="articles-col-date">Modified</div>
                <div className="articles-col-actions">Actions</div>
              </div>
              <div className="articles-loading">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="article-row-skeleton">
                    <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    const getErrorMessage = (error: any): string => {
      const status = error?.status || error?.originalStatus;
      if (status === 401) return "Please log in to view articles";
      if (status === 403) return "You do not have permission to view this resource";
      if (status === 500) return "Server error. Please try again later.";
      return "Unable to load articles. Please try again.";
    };

    return (
      <AdminLayout title="Admin Panel - Blog Articles" currentPath="/admin/blog/articles">
        <div className="articles-list-page">
          <div className="articles-error-state">
            <p className="articles-error-message">{getErrorMessage(error)}</p>
            <MagicButton
              label="Retry"
              onPress={() => refetch()}
              color="purple"
            />
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Empty state
  if (data && data.data && data.data.length === 0 && !searchTerm && statusFilter === 'all') {
    return (
      <AdminLayout title="Admin Panel - Blog Articles" currentPath="/admin/blog/articles">
        <div className="articles-list-page">
          <div className="articles-list-header">
            <div>
              <h2 className="articles-list-title">Blog Articles</h2>
              <p className="articles-list-subtitle">
                Manage your blog content and publications
              </p>
            </div>
            <MagicButton
              label="New Article"
              onPress={() => navigate('/admin/blog/articles/new')}
              color="purple"
            />
          </div>

          <div className="articles-empty-state">
            <h3>No articles yet</h3>
            <p>Create your first blog article to get started with content marketing.</p>
            <MagicButton
              label="Create First Article"
              onPress={() => navigate('/admin/blog/articles/new')}
              color="purple"
            />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Panel - Blog Articles" currentPath="/admin/blog/articles">
      <div className="articles-list-page">
        <div className="articles-list-header">
          <div>
            <h2 className="articles-list-title">Blog Articles</h2>
            <p className="articles-list-subtitle">
              Manage your blog content and publications
            </p>
          </div>
          <MagicButton
            label="New Article"
            onPress={() => navigate('/admin/blog/articles/new')}
            color="purple"
          />
        </div>

        {/* Stats Cards */}
        {stats && stats.data && (
          <div className="articles-stats">
            <div className="stat-card">
              <span className="stat-label">Published</span>
              <span className="stat-value">{stats.data.PUBLISHED}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Drafts</span>
              <span className="stat-value">{stats.data.DRAFT}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total</span>
              <span className="stat-value">{stats.data.total}</span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="articles-list-controls">
          <input
            type="text"
            placeholder="Search by title..."
            className="articles-search-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
          <div className="articles-filter-controls">
            {/* Status filter */}
            <div className="articles-filter-dropdown">
              <button
                className="dropdown-button"
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              >
                <span>{getSelectedStatusLabel()}</span>
                <ChevronDownIcon className={`dropdown-arrow ${isStatusDropdownOpen ? 'open' : ''}`} />
              </button>
              {isStatusDropdownOpen && (
                <div className="dropdown-menu">
                  {STATUS_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`dropdown-item ${statusFilter === option.value ? 'selected' : ''}`}
                      onClick={() => handleStatusFilterChange(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sort dropdown */}
            <div className="articles-filter-dropdown">
              <button
                className="dropdown-button"
                onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              >
                <span>{getSelectedSortLabel()}</span>
                <ChevronDownIcon className={`dropdown-arrow ${isSortDropdownOpen ? 'open' : ''}`} />
              </button>
              {isSortDropdownOpen && (
                <div className="dropdown-menu">
                  {SORT_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`dropdown-item ${sortBy === option.value ? 'selected' : ''}`}
                      onClick={() => handleSortChange(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sort order toggle */}
            <button
              className="sort-order-button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="articles-list-content">
          {data && data.data && data.data.length === 0 ? (
            <div className="articles-no-results">
              <p>No articles found matching your search criteria.</p>
              <MagicButton
                label="Clear Filters"
                onPress={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPage(1);
                }}
                color="grey"
              />
            </div>
          ) : (
            <>
              <div className="articles-table">
                <div className="articles-table-header">
                  <div className="articles-col-title">Title</div>
                  <div className="articles-col-status">Status</div>
                  <div className="articles-col-date">Published</div>
                  <div className="articles-col-date">Modified</div>
                  <div className="articles-col-actions">Actions</div>
                </div>
                <div className="articles-table-body">
                  {data?.data.map((article) => (
                    <div key={article.id} className="article-row">
                      <div className="articles-col-title">
                        <span className="article-row-title">{article.title}</span>
                        {article.subtitle && (
                          <span className="article-row-subtitle">{article.subtitle}</span>
                        )}
                      </div>
                      <div className="articles-col-status">
                        <span className={`status-badge ${getStatusClass(article.status)}`}>
                          {article.status}
                        </span>
                      </div>
                      <div className="articles-col-date">{formatDate(article.publishedAt)}</div>
                      <div className="articles-col-date">{formatDate(article.updatedAt)}</div>
                      <div className="articles-col-actions">
                        <button
                          className="action-button download-button"
                          onClick={() => handleDownload(article.id, article.slug, article.title)}
                          disabled={downloadingId === article.id}
                        >
                          {downloadingId === article.id ? '...' : 'DL'}
                        </button>
                        <button
                          className="action-button view-button"
                          onClick={() => navigate(`/admin/blog/articles/${article.id}/preview`)}
                        >
                          View
                        </button>
                        <button
                          className="action-button edit-button"
                          onClick={() => navigate(`/admin/blog/articles/${article.id}/edit`)}
                        >
                          Edit
                        </button>
                        <button
                          className="action-button delete-button"
                          onClick={() => handleDeleteClick(article.id, article.title)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {data && data.pagination && data.pagination.totalPages > 1 && (
                <div className="articles-pagination">
                  <Pagination
                    currentPage={page}
                    totalPages={data.pagination.totalPages}
                    onPageChange={setPage}
                  />
                  <div className="pagination-info">
                    Page {page} of {data.pagination.totalPages} ({data.pagination.totalItems} total)
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setArticleToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        articleTitle={articleToDelete?.title || ''}
        isDeleting={isDeleting}
      />
    </AdminLayout>
  );
};

export default ArticlesListPage;
